import { DateTime } from "luxon";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

/** How often "now" moves on while the screen stays open. */
const REFRESH_INTERVAL_MS = 60_000;

/**
 * Runs `refresh` every minute and whenever the page comes back into view (the tab regains focus, the phone wakes the
 * app). The latest `refresh` is the one run, so it may close over the current render's values.
 */
export const useEveryMinute = (refresh: () => void): void => {
	const latestRefresh = useRef(refresh);
	useLayoutEffect(() => {
		latestRefresh.current = refresh;
	});
	useEffect(() => {
		const run = (): void => latestRefresh.current();
		const runWhenVisible = (): void => {
			if (document.visibilityState === "visible") run();
		};
		const interval = window.setInterval(run, REFRESH_INTERVAL_MS);
		window.addEventListener("focus", run);
		document.addEventListener("visibilitychange", runWhenVisible);
		return () => {
			window.clearInterval(interval);
			window.removeEventListener("focus", run);
			document.removeEventListener("visibilitychange", runWhenVisible);
		};
	}, []);
};

/**
 * "Now", moved on every minute and whenever the page comes back into view — for a screen that states time relative to
 * it and stays open: which requests are "Today", which fall in a 48-hour window, how long until a shift "starts in".
 * Read once per build, those statements went stale while the list sat open; a minute is the finest any of them is
 * stated to.
 */
export const useNowEveryMinute = (): DateTime => {
	const [now, setNow] = useState<DateTime>(() => DateTime.now());
	useEveryMinute(() => setNow(DateTime.now()));
	return now;
};
